/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {useLocation} from 'react-router'
import {Button, Box, Stack, Skeleton, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {usePageUrls, useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import Pagination from '@salesforce/retail-react-app/app/components/pagination'
import {DEFAULT_ORDERS_SEARCH_PARAMS} from '@salesforce/retail-react-app/app/constants'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {BRAND_SHORT_LABEL} from '../../brand'
import SubscriptionsBox from '../../components/subscriptions-box'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
// import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
// import {Helmet} from 'react-helmet'

const SubscriptionHistory = () => {
    const navigate = useNavigation()
    const {locale} = useMultiSite()

    const location = useLocation()
    const {data: customer} = useCurrentCustomer()
    const searchParams = useSearchParams(DEFAULT_ORDERS_SEARCH_PARAMS)
    const {limit, offset} = searchParams[0]
    const appOrigin = useOrigin(false)
    const [subscriptions, setSubscriptions] = useState([])
    const [totalSubscriptions, setTotalSubscriptions] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const pageUrls = usePageUrls({total: totalSubscriptions, limit})
    const headingRef = useRef()
    const styles = useMultiStyleConfig('Subscriptions')
    const {getTokenWhenReady} = useAccessToken()
    // const {customParams} = getConfig()
    // const currentPath = 'https://' + customParams.currentHost + location.pathname
    // const webPageSchemaMarkup = {
    //     '@context': 'https://schema.org/',
    //     '@type': 'WebPage',
    //     name: 'Account Subscription History',
    //     description: 'View details of your account subscription history',
    //     url: currentPath,
    // }

    useEffect(() => {
        const fetchSubscriptions = async () => {
            setIsLoading(true)
            try {
                const queryParams = new URLSearchParams({
                    sfAccountEmail: customer.email,
                    brand: BRAND_SHORT_LABEL,
                    offset,
                })
                const token = await getTokenWhenReady()
                // Use the token from getTokenWhenReady for Commerce Cloud APIs
                // and accessToken for OMS APIs
                const response = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-subscription-list-oms?${queryParams.toString()}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            customerId: customer?.customerId,
                            'x-commercecloud-token': `Bearer ${token}`, // Send Commerce Cloud token as a custom header
                        },
                    },
                )

                const result = await response.json()
                if (result.success) {
                    setSubscriptions(result.subscriptions)
                    setTotalSubscriptions(result.totalSubscriptions)
                } else {
                    setError('Failed to fetch subscription list: ' + result.error)
                }
            } catch (error) {
                console.error('Error fetching subscriptions:', error)
                setError('Error fetching subscriptions: ' + error.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (customer?.email) {
            fetchSubscriptions()
        }
    }, [customer?.email])

    const hasSubscriptions = subscriptions.length > 0

    return (
        <Stack {...styles.subscriptionGap} data-testid="account-subscription-history-page">
            {/* <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet> */}
            <Text as="h1" ref={headingRef} {...styles.accountTitle}>
                <FormattedMessage
                    defaultMessage="Subscriptions"
                    id="account_subscription_history.subscriptions_title"
                />
            </Text>

            {error && (
                <Box mb={4}>
                    <Text color="red.500" fontWeight="bold">
                        {error}
                    </Text>
                </Box>
            )}

            {!hasSubscriptions && !isLoading && (
                <Stack {...styles.noSubscriptionsContainer}>
                    <Text {...styles.noSubscriptionsTitle}>
                        <FormattedMessage
                            id="account_subscription_history.no_order_yet.title"
                            defaultMessage="No subscriptions appear in history."
                        />
                    </Text>
                    <Text {...styles.noSubscriptionsDescription}>
                        <FormattedMessage
                            id="account_subscription_history.no_order_yet.description"
                            defaultMessage="Have you purchased a subscription that should appear here? For more information, contact our customer support. If you have not yet purchased a product"
                        />
                    </Text>
                    <Button
                        variant="primary"
                        onClick={() => navigate(`/${locale?.alias}`)}
                        {...styles.addButton}
                    >
                        <FormattedMessage
                            id="account_wishlist.button.start_shopping"
                            defaultMessage="Start shopping"
                        />
                    </Button>
                </Stack>
            )}

            {isLoading ? (
                [1, 2, 3].map((i) => (
                    <Stack key={i} spacing={4} layerStyle="cardBordered">
                        <Skeleton h="20px" w="112px" />
                        <Skeleton h="20px" w="200px" />
                    </Stack>
                ))
            ) : (
                <>
                    {hasSubscriptions && (
                        <>
                            {/* <SubscriptionAssistance /> */}
                            <Stack {...styles.subscriptionGap}>
                                {subscriptions.map((subscription, i) => (
                                    <SubscriptionsBox key={i} subscription={subscription} />
                                ))}

                                {subscriptions.length < totalSubscriptions && (
                                    <Box pt={4}>
                                        <Pagination
                                            currentURL={`${location.pathname}${location.search}`}
                                            urls={pageUrls}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </>
                    )}
                </>
            )}
        </Stack>
    )
}

SubscriptionHistory.getTemplateName = () => 'account-subscription-history'

export default SubscriptionHistory
